/**
 * LiveChat Pinia状态管理
 * 管理LiveChat配置、初始化和状态
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  LiveChatConfig,
  LiveChatUser,
  LiveChatInitOptions,
  LiveChatCustomVariable,
  LiveChatError,
  ChatMessage,
  ChatSession,
  ConnectionStatus,
  ChatConfig,
  ChatHistoryStorage,
  ChatStats
} from '@/types/livechat'
import { LiveChatStatus } from '@/types/livechat'
import {
  getLiveChatConfig,
  checkLiveChatAvailability
} from '@/services/livechat'
import { useUserStore } from '@/stores/user'

export const useLiveChatStore = defineStore('livechat', () => {
  // 状态定义
  const config = ref<LiveChatConfig & { loading?: boolean }>({
    licenseId: '',
    groupId: '',
    enabled: false,
    loading: false
  })

  const status = ref<LiveChatStatus>(LiveChatStatus.NOT_INITIALIZED)
  const loading = ref(false)
  const error = ref<LiveChatError | null>(null)
  const isSDKLoaded = ref(false)
  const isChatWindowOpen = ref(false)

  // 自定义聊天界面相关状态
  const messages = ref<ChatMessage[]>([])
  const connectionStatus = ref<ConnectionStatus>('disconnected')
  const chatSession = ref<ChatSession | null>(null)
  // WebSocket连接现在由API服务管理
  const chatConfig = ref<ChatConfig>({
    enabled: true,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxMessageLength: 2000,
    historyRetentionDays: 30
  })
  const isCustomChatOpen = ref(false)
  const unreadCount = ref(0)

  // 聊天历史管理相关状态
  const isLoadingHistory = ref(false)
  const hasMoreHistory = ref(true)
  const historyPageSize = ref(50)
  const currentHistoryPage = ref(0)
  const allSessions = ref<ChatSession[]>([])
  const isLoadingSessions = ref(false)

  // 计算属性
  const isAvailable = computed(() => {
    return config.value.enabled &&
      !!config.value.licenseId &&
      status.value === LiveChatStatus.INITIALIZED
  })

  const isReady = computed(() => {
    return isSDKLoaded.value &&
      status.value === LiveChatStatus.INITIALIZED
  })

  const canInitialize = computed(() => {
    return config.value.enabled &&
      !!config.value.licenseId &&
      status.value !== LiveChatStatus.INITIALIZING
  })

  // 自定义聊天界面计算属性
  const isCustomChatConnected = computed(() => connectionStatus.value === 'connected')
  const hasUnreadMessages = computed(() => unreadCount.value > 0)
  const canSendMessage = computed(() => 
    isCustomChatConnected.value && chatSession.value !== null
  )

  // 聊天历史管理计算属性
  const totalMessages = computed(() => messages.value.length)
  const userMessages = computed(() => messages.value.filter(m => m.isUser))
  const agentMessages = computed(() => messages.value.filter(m => !m.isUser && m.type !== 'system'))
  const systemMessages = computed(() => messages.value.filter(m => m.type === 'system'))
  const chatStats = computed((): ChatStats => ({
    totalMessages: totalMessages.value,
    userMessages: userMessages.value.length,
    agentMessages: agentMessages.value.length,
    unreadMessages: unreadCount.value,
    sessionStartTime: chatSession.value ? new Date(chatSession.value.createdAt).getTime() : undefined,
    lastActivityTime: messages.value.length > 0 ? Math.max(...messages.value.map(m => m.timestamp)) : undefined
  }))

  /**
   * 从API加载LiveChat配置
   */
  const loadConfig = async (): Promise<void> => {
    try {
      loading.value = true
      config.value.loading = true
      error.value = null

      const configData = await getLiveChatConfig()
      config.value = { ...configData, loading: false }

      console.log('LiveChat配置加载成功:', configData)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '加载LiveChat配置失败'
      error.value = {
        code: 'CONFIG_LOAD_FAILED',
        message: errorMessage,
        details: err
      }
      config.value.loading = false
      console.error('加载LiveChat配置失败:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 动态加载LiveChat SDK
   * 內部使用：帶指數退避重試（2s/4s/8s 三次）
   */
  const loadLiveChatSDK = (): Promise<void> => {
    return loadLiveChatSDKWithRetry(0)
  }

  /**
   * 帶重試的 SDK 加載
   * @param attempt 當前嘗試次數（0 起算）
   */
  const loadLiveChatSDKWithRetry = async (attempt: number): Promise<void> => {
    const MAX_RETRIES = 3
    try {
      await loadLiveChatSDKOnce()
    } catch (err) {
      if (attempt >= MAX_RETRIES - 1) {
        // 已達最大重試次數，向上拋
        throw err
      }
      // 指數退避：2s、4s、8s
      const backoffMs = Math.pow(2, attempt + 1) * 1000
      console.warn(`LiveChat SDK 加載失敗（第 ${attempt + 1} 次），${backoffMs}ms 後重試...`, err)
      await new Promise(r => setTimeout(r, backoffMs))
      return loadLiveChatSDKWithRetry(attempt + 1)
    }
  }

  /**
   * 單次 SDK 加載嘗試
   */
  const loadLiveChatSDKOnce = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 如果SDK已经加载，直接返回
      if (window.LC_API || isSDKLoaded.value) {
        isSDKLoaded.value = true
        resolve()
        return
      }

      // 检查配置是否有效
      if (!config.value.licenseId) {
        reject(new Error('LiveChat License ID未配置'))
        return
      }

      // SDK 加載超時時間（15 秒）
      const SDK_LOAD_TIMEOUT = 15000
      // LC_API 就緒等待超時（10 秒）
      const API_READY_TIMEOUT = 10000

      try {
        // 设置LiveChat配置
        window.__lc = window.__lc || { license: '' }
        window.__lc.license = config.value.licenseId

        if (config.value.groupId) {
          window.__lc.group = config.value.groupId
        }

        // 檢查是否已有腳本標籤（避免重複加載）
        const existingScript = document.querySelector('script[src*="livechatinc.com/tracking.js"]')
        if (existingScript) {
          // 腳本已存在，只需等待 LC_API 就緒
          const startTime = Date.now()
          const checkAPI = () => {
            if (window.LC_API) {
              isSDKLoaded.value = true
              console.log('LiveChat SDK已就緒（復用已有腳本）')
              resolve()
            } else if (Date.now() - startTime > API_READY_TIMEOUT) {
              // 超時：移除舊腳本，重新加載
              existingScript.remove()
              loadFreshScript()
            } else {
              setTimeout(checkAPI, 200)
            }
          }
          checkAPI()
          return
        }

        loadFreshScript()

        function loadFreshScript() {
          // 創建並加載SDK腳本
          const script = document.createElement('script')
          script.src = 'https://cdn.livechatinc.com/tracking.js'
          script.async = true
          script.defer = true

          // 整體超時保護
          const loadTimeout = setTimeout(() => {
            script.onload = null
            script.onerror = null
            console.warn('LiveChat SDK 加載超時，嘗試繼續...')
            // 超時後檢查一次 LC_API 是否已可用
            if (window.LC_API) {
              isSDKLoaded.value = true
              resolve()
            } else {
              reject(new Error('LiveChat SDK 加載超時'))
            }
          }, SDK_LOAD_TIMEOUT)

          script.onload = () => {
            // 等待LC_API可用，帶超時保護
            const startTime = Date.now()
            const checkAPI = () => {
              if (window.LC_API) {
                clearTimeout(loadTimeout)
                isSDKLoaded.value = true
                console.log('LiveChat SDK加载成功')
                resolve()
              } else if (Date.now() - startTime > API_READY_TIMEOUT) {
                clearTimeout(loadTimeout)
                console.warn('LiveChat LC_API 等待超時')
                reject(new Error('LiveChat API 初始化超時'))
              } else {
                setTimeout(checkAPI, 200)
              }
            }
            checkAPI()
          }

          script.onerror = () => {
            clearTimeout(loadTimeout)
            const errorMsg = 'LiveChat SDK加载失败（CDN 不可達）'
            console.error(errorMsg)
            reject(new Error(errorMsg))
          }

          // 添加脚本到页面
          document.head.appendChild(script)
        }

      } catch (err) {
        console.error('LiveChat SDK初始化失败:', err)
        reject(err)
      }
    })
  }

  // 用於去重併發初始化的 in-flight Promise
  let _initPromise: Promise<void> | null = null

  /**
   * 初始化LiveChat（in-flight 復用：併發點擊共享同一個 Promise）
   */
  const initializeChat = (options: LiveChatInitOptions = {}): Promise<void> => {
    // 如果已經初始化完成，直接返回成功
    if (status.value === LiveChatStatus.INITIALIZED && isSDKLoaded.value) {
      return Promise.resolve()
    }

    // 如果已有正在進行的初始化，復用同一個 Promise
    if (_initPromise) {
      return _initPromise
    }

    _initPromise = doInitializeChat(options).finally(() => {
      _initPromise = null
    })

    return _initPromise
  }

  /**
   * 實際執行初始化邏輯
   */
  const doInitializeChat = async (options: LiveChatInitOptions = {}): Promise<void> => {
    try {
      // 配置必須可用
      if (!config.value.enabled || !config.value.licenseId) {
        throw new Error('LiveChat configuration is not available')
      }

      status.value = LiveChatStatus.INITIALIZING
      error.value = null

      // 加载SDK（內含重試）
      if (!isSDKLoaded.value) {
        await loadLiveChatSDK()
      }

      // 验证用户身份信息
      const validationResult = validateUserIdentity(options.user || null)
      if (validationResult.warnings.length > 0) {
        console.warn('用户身份验证警告:', validationResult.warnings)
      }

      if (!validationResult.isValid) {
        console.error('用户身份验证失败:', validationResult.missingFields)
        // 即使验证失败也继续初始化，但记录错误
        error.value = {
          code: 'IDENTITY_VALIDATION_FAILED',
          message: `用户身份信息不完整: ${validationResult.missingFields.join(', ')}`,
          details: validationResult
        }
      }

      // 传递用户身份信息
      const transmissionResult = await transmitUserIdentity(options.user || null)
      if (!transmissionResult.success) {
        console.warn('用户身份信息传递失败，但继续初始化:', transmissionResult.error)
      }

      // 设置事件监听
      setupEventListeners()

      status.value = LiveChatStatus.INITIALIZED
      console.log('LiveChat初始化成功', {
        userType: options.user ? 'authenticated' : 'guest',
        identityTransmission: transmissionResult.success ? 'success' : 'failed',
        transmittedData: transmissionResult.transmittedData
      })

      // 如果需要自动打开聊天窗口
      if (options.autoOpen) {
        openChatWindow()
      }

    } catch (err) {
      status.value = LiveChatStatus.FAILED
      const errorMessage = err instanceof Error ? err.message : 'LiveChat初始化失败'
      error.value = {
        code: 'INITIALIZATION_FAILED',
        message: errorMessage,
        details: err
      }
      console.error('LiveChat初始化失败:', err)
      throw err
    }
  }

  /**
   * 设置用户信息
   */
  const setUserInfo = async (user: LiveChatUser): Promise<void> => {
    if (!window.LC_API) {
      throw new Error('LiveChat API未就绪')
    }

    let hasErrors = false
    const errors: string[] = []

    // 设置访客基本信息（允许部分失败）
    try {
      if (user.name && typeof (window.LC_API as any).set_name === 'function') {
        (window.LC_API as any).set_name(user.name)
      } else if (user.name && typeof window.LC_API.set_visitor_name === 'function') {
        window.LC_API.set_visitor_name(user.name)
      }
    } catch (err) {
      hasErrors = true
      errors.push('设置访客名称失败')
      console.error('设置访客名称失败:', err)
    }

    try {
      if (user.email && typeof (window.LC_API as any).set_email === 'function') {
        (window.LC_API as any).set_email(user.email)
      } else if (user.email && typeof window.LC_API.set_visitor_email === 'function') {
        window.LC_API.set_visitor_email(user.email)
      }
    } catch (err) {
      hasErrors = true
      errors.push('设置访客邮箱失败')
      console.error('设置访客邮箱失败:', err)
    }

    // 准备自定义变量数组
    const customVariables: LiveChatCustomVariable[] = [
      { name: 'user_id', value: user.id },
      { name: 'user_name', value: user.name },
      { name: 'user_email', value: user.email },
      { name: 'registered_at', value: user.registeredAt },
      { name: 'user_type', value: 'authenticated' },
      { name: 'session_start', value: new Date().toISOString() }
    ]

    // 添加额外的用户信息（如果可用）
    const userStore = useUserStore()
    if (userStore.profile) {
      // 添加用户等级信息
      if (userStore.currentMemberLevel) {
        customVariables.push({
          name: 'member_level',
          value: userStore.currentMemberLevel
        })
      }

      // 添加用户状态信息
      customVariables.push({
        name: 'account_status',
        value: userStore.isAccountFrozen ? 'frozen' : 'active'
      })

      // 添加钱包信息
      if (userStore.walletInfo) {
        customVariables.push({
          name: 'carbon_credits',
          value: userStore.walletInfo.carbon_credits?.toString() || '0'
        })
        customVariables.push({
          name: 'balance',
          value: userStore.walletInfo.available_balance?.toString() || '0'
        })
      }

      // 添加用户语言偏好（如果有）
      customVariables.push({
        name: 'language',
        value: navigator.language || 'en'
      })

      // 添加用户时区
      customVariables.push({
        name: 'timezone',
        value: Intl.DateTimeFormat().resolvedOptions().timeZone
      })
    }

    // 设置自定义变量（这是最重要的部分）
    try {
      if (typeof window.LC_API?.set_custom_variables === 'function') {
        window.LC_API.set_custom_variables(customVariables)
      } else if (typeof (window.LC_API as any).update_visitor_info === 'function') {
        // 备用方法：使用update_visitor_info
        const visitorInfo: any = {}
        customVariables.forEach(variable => {
          visitorInfo[variable.name] = variable.value
        })
          ; (window.LC_API as any).update_visitor_info(visitorInfo)
      }
      console.log('用户信息设置成功:', {
        user,
        customVariables: customVariables.length
      })
    } catch (err) {
      hasErrors = true
      errors.push('设置自定义变量失败')
      console.error('设置自定义变量失败:', err)
    }

    // 如果有错误，记录但不阻止聊天功能
    if (hasErrors) {
      error.value = {
        code: 'USER_INFO_FAILED',
        message: `用户信息传递部分失败: ${errors.join(', ')}，但聊天功能仍可使用`,
        details: errors
      }
    }
  }

  /**
   * 设置访客模式信息
   */
  const setGuestInfo = (): void => {
    try {
      if (!window.LC_API) {
        throw new Error('LiveChat API未就绪')
      }

      // 生成访客ID
      const guestId = `guest_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`

      const guestVariables: LiveChatCustomVariable[] = [
        { name: 'user_type', value: 'guest' },
        { name: 'guest_id', value: guestId },
        { name: 'visit_time', value: new Date().toISOString() },
        { name: 'session_start', value: new Date().toISOString() },
        { name: 'user_agent', value: navigator.userAgent },
        { name: 'language', value: navigator.language || 'en' },
        { name: 'timezone', value: Intl.DateTimeFormat().resolvedOptions().timeZone },
        { name: 'screen_resolution', value: `${screen.width}x${screen.height}` },
        { name: 'referrer', value: document.referrer || 'direct' },
        { name: 'page_url', value: window.location.href }
      ]

      // 设置访客名称（如果没有设置）
      window.LC_API.set_visitor_name(`访客 ${guestId.split('_')[1]}`)

      if (typeof window.LC_API?.set_custom_variables === 'function') {
        window.LC_API.set_custom_variables(guestVariables)
      }
      console.log('访客信息设置成功:', {
        guestId,
        variablesCount: guestVariables.length
      })

    } catch (err) {
      console.error('设置访客信息失败:', err)
      // 记录错误但不阻止聊天功能
      error.value = {
        code: 'GUEST_INFO_FAILED',
        message: '访客信息设置失败，但聊天功能仍可使用',
        details: err
      }
    }
  }

  /**
   * 打开聊天窗口
   */
  const openChatWindow = (): void => {
    try {
      if (!isReady.value) {
        throw new Error('LiveChat未就绪')
      }

      window.LC_API.open_chat_window()
      isChatWindowOpen.value = true
      console.log('聊天窗口已打开')

    } catch (err) {
      console.error('打开聊天窗口失败:', err)
      throw err
    }
  }

  /**
   * 关闭聊天窗口
   */
  const closeChatWindow = (): void => {
    try {
      if (!window.LC_API) {
        return
      }

      window.LC_API.close_chat_window()
      isChatWindowOpen.value = false
      console.log('聊天窗口已关闭')

    } catch (err) {
      console.error('关闭聊天窗口失败:', err)
    }
  }

  /**
   * 最小化聊天窗口
   */
  const minimizeChatWindow = (): void => {
    try {
      if (!window.LC_API) {
        return
      }

      window.LC_API.minimize_chat_window()
      console.log('聊天窗口已最小化')

    } catch (err) {
      console.error('最小化聊天窗口失败:', err)
    }
  }

  /**
   * 设置事件监听器
   */
  const setupEventListeners = (): void => {
    try {
      if (!window.LC_API) {
        return
      }

      // 检查并设置聊天窗口打开事件
      if (typeof (window.LC_API as any).on_chat_window_opened === 'function') {
        ; (window.LC_API as any).on_chat_window_opened(() => {
          isChatWindowOpen.value = true
          console.log('聊天窗口已打开')
        })
      }

      // 检查并设置聊天窗口最小化事件
      if (typeof (window.LC_API as any).on_chat_window_minimized === 'function') {
        ; (window.LC_API as any).on_chat_window_minimized(() => {
          isChatWindowOpen.value = false
          console.log('聊天窗口已最小化')
        })
      }

      // 检查并设置聊天窗口隐藏事件
      if (typeof (window.LC_API as any).on_chat_window_hidden === 'function') {
        ; (window.LC_API as any).on_chat_window_hidden(() => {
          isChatWindowOpen.value = false
          console.log('聊天窗口已隐藏')
        })
      }

      // 检查并设置聊天开始事件
      if (typeof (window.LC_API as any).on_chat_started === 'function') {
        ; (window.LC_API as any).on_chat_started(() => {
          console.log('聊天已开始')
        })
      }

      // 检查并设置聊天结束事件
      if (typeof (window.LC_API as any).on_chat_ended === 'function') {
        ; (window.LC_API as any).on_chat_ended(() => {
          console.log('聊天已结束')
        })
      }

      console.log('事件监听器设置完成')

    } catch (err) {
      console.error('设置事件监听器失败:', err)
    }
  }

  /**
   * 检查LiveChat可用性
   */
  const checkAvailability = async (): Promise<boolean> => {
    try {
      return await checkLiveChatAvailability()
    } catch (err) {
      console.error('检查LiveChat可用性失败:', err)
      return false
    }
  }

  /**
   * 重置状态
   */
  const reset = (): void => {
    config.value = {
      licenseId: '',
      groupId: '',
      enabled: false
    }
    status.value = LiveChatStatus.NOT_INITIALIZED
    loading.value = false
    error.value = null
    isSDKLoaded.value = false
    isChatWindowOpen.value = false
  }

  /**
   * 清除错误状态
   */
  const clearError = (): void => {
    error.value = null
  }

  /**
   * 从用户store获取当前用户信息并转换为LiveChat格式
   */
  const getCurrentUserForLiveChat = (): LiveChatUser | null => {
    try {
      const userStore = useUserStore()
      const userProfile = userStore.profile

      if (!userProfile) {
        return null
      }

      return {
        id: userProfile.id.toString(),
        name: `${userProfile.first_name} ${userProfile.last_name}`.trim(),
        email: userProfile.email,
        registeredAt: userProfile.created_at
      }
    } catch (err) {
      console.error('获取用户信息失败:', err)
      return null
    }
  }

  /**
   * 传递用户身份信息到LiveChat
   * 包含完整的用户信息和错误处理
   */
  const transmitUserIdentity = async (user: LiveChatUser | null): Promise<{
    success: boolean
    error?: string
    transmittedData?: Record<string, any>
  }> => {
    try {
      if (!window.LC_API) {
        throw new Error('LiveChat API未就绪')
      }

      const transmittedData: Record<string, any> = {}

      if (user) {
        // 已登录用户身份传递
        await setUserInfo(user)
        transmittedData.userType = 'authenticated'
        transmittedData.userId = user.id
        transmittedData.userName = user.name
        transmittedData.userEmail = user.email
        transmittedData.registeredAt = user.registeredAt
      } else {
        // 访客身份传递
        setGuestInfo()
        transmittedData.userType = 'guest'
        transmittedData.guestId = `guest_${Date.now()}`
      }

      // 添加会话信息
      transmittedData.sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
      transmittedData.transmissionTime = new Date().toISOString()
      transmittedData.platform = 'web'
      transmittedData.source = 'carbon-trading-platform'

      console.log('用户身份信息传递成功:', transmittedData)

      return {
        success: true,
        transmittedData
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '身份信息传递失败'
      console.error('用户身份信息传递失败:', err)

      // 记录错误但不阻止聊天功能
      error.value = {
        code: 'IDENTITY_TRANSMISSION_FAILED',
        message: errorMessage,
        details: err
      }

      return {
        success: false,
        error: errorMessage
      }
    }
  }

  /**
   * 验证用户身份信息完整性
   */
  const validateUserIdentity = (user: LiveChatUser | null): {
    isValid: boolean
    missingFields: string[]
    warnings: string[]
  } => {
    const missingFields: string[] = []
    const warnings: string[] = []

    if (!user) {
      return {
        isValid: true, // 访客模式也是有效的
        missingFields: [],
        warnings: ['用户未登录，将使用访客模式']
      }
    }

    // 检查必需字段
    if (!user.id) missingFields.push('用户ID')
    if (!user.name || user.name.trim() === '') missingFields.push('用户姓名')
    if (!user.email) missingFields.push('用户邮箱')
    if (!user.registeredAt) missingFields.push('注册时间')

    // 检查邮箱格式
    if (user.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
      warnings.push('邮箱格式可能不正确')
    }

    // 检查姓名长度
    if (user.name && user.name.length > 50) {
      warnings.push('用户姓名过长，可能被截断')
    }

    return {
      isValid: missingFields.length === 0,
      missingFields,
      warnings
    }
  }

  /**
   * 使用当前登录用户信息初始化LiveChat
   */
  const initializeChatWithCurrentUser = async (autoOpen: boolean = false): Promise<void> => {
    const currentUser = getCurrentUserForLiveChat()

    if (currentUser) {
      await initializeChat({ user: currentUser, autoOpen })
    } else {
      // 如果没有用户信息，使用访客模式初始化
      await initializeChat({ autoOpen })
      setGuestInfo()
    }
  }

  // ==================== 自定义聊天界面方法 ====================

  /**
   * 初始化自定义聊天界面 - 使用 LiveChat 官方 SDK
   */
  const initializeCustomChat = async (user: LiveChatUser | null): Promise<void> => {
    try {
      connectionStatus.value = 'connecting'
      error.value = null

      // 使用新的官方 SDK API 服务
      const { liveChatApi } = await import('@/services/livechat-api')
      
      // 创建会话（这会初始化 LiveChat SDK）
      const session = await liveChatApi.createSession(user)
      chatSession.value = session

      // 设置消息和状态处理器
      liveChatApi.onMessage(receiveMessage)
      liveChatApi.onStatusChange((status) => {
        connectionStatus.value = status
      })

      // 连接到 LiveChat（这会设置 SDK 事件监听器）
      await liveChatApi.connectWebSocket(session.id)

      connectionStatus.value = 'connected'
      console.log('LiveChat 官方 SDK 初始化成功', { sessionId: session.id })

    } catch (err) {
      connectionStatus.value = 'error'
      const errorMessage = err instanceof Error ? err.message : 'LiveChat 初始化失败'
      error.value = {
        code: 'LIVECHAT_INIT_FAILED',
        message: errorMessage,
        details: err
      }
      console.error('LiveChat 官方 SDK 初始化失败:', err)
      throw err
    }
  }

  /**
   * 创建聊天会话（已移至API服务）
   */
  const createChatSession = async (user: LiveChatUser | null): Promise<ChatSession> => {
    const { liveChatApi } = await import('@/services/livechat-api')
    const session = await liveChatApi.createSession(user)

    // 保存会话信息到本地存储
    try {
      localStorage.setItem('livechat_session', JSON.stringify(session))
    } catch (err) {
      console.warn('保存会话信息到本地存储失败:', err)
    }

    return session
  }

  /**
   * 发送文字消息
   */
  const sendTextMessage = async (content: string, user: LiveChatUser | null): Promise<void> => {
    if (!chatSession.value) {
      throw new Error('聊天会话未初始化')
    }

    if (!content.trim()) {
      throw new Error('消息内容不能为空')
    }

    if (content.length > chatConfig.value.maxMessageLength) {
      throw new Error(`消息长度不能超过${chatConfig.value.maxMessageLength}个字符`)
    }

    // 创建临时消息
    const tempMessage: ChatMessage = {
      id: `temp_${Date.now()}`,
      content: content.trim(),
      type: 'text',
      isUser: true,
      timestamp: Date.now(),
      status: 'sending',
      sender: user ? {
        id: user.id,
        name: user.name
      } : undefined
    }

    // 立即添加到消息列表（乐观更新）
    messages.value.push(tempMessage)
    saveChatHistory()

    try {
      // 调用LiveChat API发送消息
      const { liveChatApi } = await import('@/services/livechat-api')
      const sentMessage = await liveChatApi.sendTextMessage({
        sessionId: chatSession.value.id,
        content: content.trim(),
        type: 'text',
        user
      })

      // 更新消息状态为已发送
      const messageIndex = messages.value.findIndex(m => m.id === tempMessage.id)
      if (messageIndex !== -1) {
        messages.value[messageIndex] = {
          ...sentMessage,
          status: 'sent'
        }
      }

      saveChatHistory()
      console.log('文字消息发送成功:', content)

    } catch (err) {
      // 标记消息发送失败
      const messageIndex = messages.value.findIndex(m => m.id === tempMessage.id)
      if (messageIndex !== -1) {
        messages.value[messageIndex].status = 'failed'
      }
      
      const errorMessage = err instanceof Error ? err.message : '消息发送失败'
      console.error('发送文字消息失败:', err)
      throw new Error(errorMessage)
    }
  }

  /**
   * 发送图片消息 - LiveChat 官方 SDK 暂不支持文件上传
   * 改为发送文字消息提示用户使用官方聊天窗口上传文件
   */
  const sendImageMessage = async (file: File, user: LiveChatUser | null): Promise<void> => {
    if (!chatSession.value) {
      throw new Error('聊天会话未初始化')
    }

    // 验证文件
    validateImageFile(file)

    try {
      // 发送提示消息，告知用户需要使用官方聊天窗口上传文件
      const message = `I would like to send an image file: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB). Please open the official chat window to upload files.`
      
      await sendTextMessage(message, user)
      
      console.log('图片上传提示消息发送成功:', file.name)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '图片发送失败'
      console.error('发送图片消息失败:', err)
      throw new Error(errorMessage)
    }
  }

  /**
   * 接收消息
   */
  const receiveMessage = (message: ChatMessage): void => {
    // 检查消息是否已存在
    const existingMessage = messages.value.find(m => m.id === message.id)
    if (existingMessage) {
      return
    }

    // 添加消息到列表
    const newMessage: ChatMessage = {
      ...message,
      read: false
    }

    messages.value.push(newMessage)

    // 如果不是用户发送的消息，增加未读计数
    if (!message.isUser) {
      unreadCount.value++
    }

    saveChatHistory()
    console.log('收到新消息:', message)
  }

  /**
   * 验证图片文件
   */
  const validateImageFile = (file: File): void => {
    // 检查文件是否存在
    if (!file) {
      throw new Error('未选择文件')
    }

    // 检查文件大小
    if (file.size > chatConfig.value.maxFileSize) {
      throw new Error(`文件大小不能超过${Math.round(chatConfig.value.maxFileSize / 1024 / 1024)}MB`)
    }

    // 检查文件类型
    if (!chatConfig.value.allowedFileTypes.includes(file.type)) {
      throw new Error('不支持的文件类型，请选择图片文件 (JPEG, PNG, GIF, WebP)')
    }

    // 检查文件名长度
    if (file.name.length > 255) {
      throw new Error('文件名过长，最大支持255个字符')
    }

    // 检查是否为空文件
    if (file.size === 0) {
      throw new Error('文件为空，请选择有效的图片文件')
    }

    // 检查文件名是否包含特殊字符
    const invalidChars = /[<>:"/\\|?*]/
    if (invalidChars.test(file.name)) {
      throw new Error('文件名包含无效字符')
    }
  }



  /**
   * 建立WebSocket连接（已移至API服务）
   */
  const connectWebSocket = async (): Promise<void> => {
    if (!chatSession.value) {
      return
    }

    try {
      const { liveChatApi } = await import('@/services/livechat-api')
      await liveChatApi.connectWebSocket(chatSession.value.id)
    } catch (err) {
      console.error('WebSocket连接失败:', err)
    }
  }

  /**
   * 断开WebSocket连接
   */
  const disconnectWebSocket = async (): Promise<void> => {
    try {
      const { liveChatApi } = await import('@/services/livechat-api')
      liveChatApi.disconnectWebSocket()
      
      // 清理处理器
      liveChatApi.removeMessageHandler(receiveMessage)
    } catch (err) {
      console.error('断开WebSocket连接失败:', err)
    }
    
    connectionStatus.value = 'disconnected'
  }

  /**
   * 保存聊天历史到本地存储
   */
  const saveChatHistory = (): void => {
    if (!chatSession.value) {
      return
    }

    try {
      const historyData: ChatHistoryStorage = {
        sessionId: chatSession.value.id,
        messages: messages.value,
        timestamp: Date.now(),
        userId: chatSession.value.userId
      }

      const storageKey = `livechat_history_${chatSession.value.id}`
      localStorage.setItem(storageKey, JSON.stringify(historyData))

      // 清理过期的历史记录
      cleanupExpiredHistory()

    } catch (err) {
      console.error('保存聊天历史失败:', err)
    }
  }

  /**
   * 加载聊天历史
   */
  const loadChatHistory = async (reset: boolean = false): Promise<void> => {
    if (!chatSession.value) {
      return
    }

    try {
      isLoadingHistory.value = true

      if (reset) {
        messages.value = []
        currentHistoryPage.value = 0
        hasMoreHistory.value = true
      }

      // 先从本地存储加载
      const localHistory = loadLocalChatHistory()
      if (localHistory.length > 0 && reset) {
        messages.value = localHistory
        updateUnreadCount()
      }

      // 然后从API获取最新历史
      const { liveChatApi } = await import('@/services/livechat-api')
      const apiHistory = await liveChatApi.getChatHistory(
        chatSession.value.id, 
        historyPageSize.value
      )
      
      if (apiHistory.length > 0) {
        if (reset) {
          // 合并本地和API历史，去重
          const allMessages = [...localHistory, ...apiHistory]
          const uniqueMessages = allMessages.filter((message, index, self) => 
            index === self.findIndex(m => m.id === message.id)
          )
          
          // 按时间戳排序
          uniqueMessages.sort((a, b) => a.timestamp - b.timestamp)
          
          messages.value = uniqueMessages
        } else {
          // 分页加载，添加到现有消息前面
          const newMessages = apiHistory.filter(apiMsg => 
            !messages.value.some(existingMsg => existingMsg.id === apiMsg.id)
          )
          
          if (newMessages.length > 0) {
            // 按时间戳排序并添加到消息列表前面
            newMessages.sort((a, b) => a.timestamp - b.timestamp)
            messages.value = [...newMessages, ...messages.value]
          }
        }
        
        updateUnreadCount()
        saveChatHistory()
        
        // 检查是否还有更多历史
        hasMoreHistory.value = apiHistory.length === historyPageSize.value
        currentHistoryPage.value++
      } else {
        hasMoreHistory.value = false
      }

    } catch (err) {
      console.error('加载聊天历史失败:', err)
      hasMoreHistory.value = false
    } finally {
      isLoadingHistory.value = false
    }
  }

  /**
   * 加载更多历史消息（分页）
   */
  const loadMoreHistory = async (): Promise<void> => {
    if (!chatSession.value || !hasMoreHistory.value || isLoadingHistory.value) {
      return
    }

    try {
      isLoadingHistory.value = true

      const { liveChatApi } = await import('@/services/livechat-api')
      
      // 获取最早消息的时间戳作为分页参数
      const oldestMessage = messages.value[0]
      const beforeTimestamp = oldestMessage ? oldestMessage.timestamp : Date.now()
      
      const olderMessages = await liveChatApi.getChatHistoryBefore(
        chatSession.value.id,
        beforeTimestamp,
        historyPageSize.value
      )
      
      if (olderMessages.length > 0) {
        // 过滤掉已存在的消息
        const newMessages = olderMessages.filter(msg => 
          !messages.value.some(existingMsg => existingMsg.id === msg.id)
        )
        
        if (newMessages.length > 0) {
          // 按时间戳排序并添加到消息列表前面
          newMessages.sort((a, b) => a.timestamp - b.timestamp)
          messages.value = [...newMessages, ...messages.value]
          saveChatHistory()
        }
        
        // 检查是否还有更多历史
        hasMoreHistory.value = olderMessages.length === historyPageSize.value
        currentHistoryPage.value++
      } else {
        hasMoreHistory.value = false
      }

    } catch (err) {
      console.error('加载更多历史消息失败:', err)
      hasMoreHistory.value = false
    } finally {
      isLoadingHistory.value = false
    }
  }

  /**
   * 从本地存储加载聊天历史
   */
  const loadLocalChatHistory = (): ChatMessage[] => {
    if (!chatSession.value) {
      return []
    }

    try {
      const storageKey = `livechat_history_${chatSession.value.id}`
      const stored = localStorage.getItem(storageKey)
      
      if (stored) {
        const data: ChatHistoryStorage = JSON.parse(stored)
        
        // 检查数据是否过期
        const maxAge = chatConfig.value.historyRetentionDays * 24 * 60 * 60 * 1000
        if (Date.now() - data.timestamp < maxAge) {
          return data.messages || []
        }
      }
    } catch (err) {
      console.error('加载本地聊天历史失败:', err)
    }

    return []
  }

  /**
   * 获取所有聊天会话
   */
  const getAllChatSessions = async (): Promise<ChatSession[]> => {
    try {
      isLoadingSessions.value = true

      // 先从本地存储获取会话列表
      const localSessions = loadLocalChatSessions()
      allSessions.value = localSessions

      // 然后从API获取最新会话列表
      const { liveChatApi } = await import('@/services/livechat-api')
      const apiSessions = await liveChatApi.getAllSessions()
      
      if (apiSessions.length > 0) {
        // 合并本地和API会话，去重
        const allSessionsData = [...localSessions, ...apiSessions]
        const uniqueSessions = allSessionsData.filter((session, index, self) => 
          index === self.findIndex(s => s.id === session.id)
        )
        
        // 按创建时间排序（最新的在前）
        uniqueSessions.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        
        allSessions.value = uniqueSessions
        saveLocalChatSessions(uniqueSessions)
      }

      return allSessions.value

    } catch (err) {
      console.error('获取聊天会话列表失败:', err)
      return allSessions.value
    } finally {
      isLoadingSessions.value = false
    }
  }

  /**
   * 切换到指定会话
   */
  const switchToSession = async (sessionId: string): Promise<void> => {
    try {
      // 保存当前会话的历史
      if (chatSession.value) {
        saveChatHistory()
      }

      // 查找目标会话
      const targetSession = allSessions.value.find(s => s.id === sessionId)
      if (!targetSession) {
        throw new Error(`会话 ${sessionId} 不存在`)
      }

      // 切换会话
      chatSession.value = targetSession
      
      // 重置消息和分页状态
      messages.value = []
      currentHistoryPage.value = 0
      hasMoreHistory.value = true
      unreadCount.value = 0

      // 加载新会话的历史
      await loadChatHistory(true)

      console.log('已切换到会话:', sessionId)

    } catch (err) {
      console.error('切换会话失败:', err)
      throw err
    }
  }

  /**
   * 删除指定会话
   */
  const deleteSession = async (sessionId: string): Promise<void> => {
    try {
      // 从API删除会话
      const { liveChatApi } = await import('@/services/livechat-api')
      await liveChatApi.deleteSession(sessionId)

      // 从本地存储删除会话历史
      const storageKey = `livechat_history_${sessionId}`
      localStorage.removeItem(storageKey)

      // 从会话列表中移除
      allSessions.value = allSessions.value.filter(s => s.id !== sessionId)
      saveLocalChatSessions(allSessions.value)

      // 如果删除的是当前会话，需要切换到其他会话或创建新会话
      if (chatSession.value?.id === sessionId) {
        if (allSessions.value.length > 0) {
          await switchToSession(allSessions.value[0].id)
        } else {
          // 创建新会话
          const currentUser = getCurrentUserForLiveChat()
          await initializeCustomChat(currentUser)
        }
      }

      console.log('会话已删除:', sessionId)

    } catch (err) {
      console.error('删除会话失败:', err)
      throw err
    }
  }

  /**
   * 从本地存储加载会话列表
   */
  const loadLocalChatSessions = (): ChatSession[] => {
    try {
      const stored = localStorage.getItem('livechat_sessions')
      if (stored) {
        const sessions: ChatSession[] = JSON.parse(stored)
        
        // 过滤掉过期的会话
        const maxAge = chatConfig.value.historyRetentionDays * 24 * 60 * 60 * 1000
        const validSessions = sessions.filter(session => {
          const sessionAge = Date.now() - new Date(session.createdAt).getTime()
          return sessionAge < maxAge
        })
        
        return validSessions
      }
    } catch (err) {
      console.error('加载本地会话列表失败:', err)
    }
    
    return []
  }

  /**
   * 保存会话列表到本地存储
   */
  const saveLocalChatSessions = (sessions: ChatSession[]): void => {
    try {
      localStorage.setItem('livechat_sessions', JSON.stringify(sessions))
    } catch (err) {
      console.error('保存会话列表失败:', err)
    }
  }

  /**
   * 清理过期的历史记录
   */
  const cleanupExpiredHistory = (): void => {
    try {
      const maxAge = chatConfig.value.historyRetentionDays * 24 * 60 * 60 * 1000
      const keys = Object.keys(localStorage)
      
      keys.forEach(key => {
        if (key.startsWith('livechat_history_')) {
          try {
            const stored = localStorage.getItem(key)
            if (stored) {
              const data: ChatHistoryStorage = JSON.parse(stored)
              if (Date.now() - data.timestamp > maxAge) {
                localStorage.removeItem(key)
                console.log('清理过期聊天历史:', key)
              }
            }
          } catch (err) {
            // 如果解析失败，删除该项
            localStorage.removeItem(key)
          }
        }
      })

      // 清理过期的会话列表
      const sessions = loadLocalChatSessions()
      if (sessions.length !== allSessions.value.length) {
        saveLocalChatSessions(sessions)
        allSessions.value = sessions
      }
    } catch (err) {
      console.error('清理过期历史记录失败:', err)
    }
  }

  /**
   * 更新未读消息计数
   */
  const updateUnreadCount = (): void => {
    unreadCount.value = messages.value.filter(m => !m.isUser && !m.read).length
  }

  /**
   * 标记所有消息为已读
   */
  const markAllMessagesAsRead = (): void => {
    messages.value.forEach(message => {
      if (!message.isUser) {
        message.read = true
      }
    })
    unreadCount.value = 0
    saveChatHistory()
  }

  /**
   * 打开自定义聊天界面
   */
  const openCustomChat = async (): Promise<void> => {
    try {
      isCustomChatOpen.value = true
      
      // 如果还没有初始化，先初始化
      if (connectionStatus.value === 'disconnected') {
        const currentUser = getCurrentUserForLiveChat()
        await initializeCustomChat(currentUser)
      }

      // 标记消息为已读
      markAllMessagesAsRead()

    } catch (err) {
      console.error('打开自定义聊天界面失败:', err)
      throw err
    }
  }

  /**
   * 关闭自定义聊天界面
   */
  const closeCustomChat = (): void => {
    isCustomChatOpen.value = false
    // 保持连接，不断开WebSocket
  }



  return {
    // 原有状态
    config,
    status,
    loading,
    error,
    isSDKLoaded,
    isChatWindowOpen,

    // 自定义聊天界面状态
    messages,
    connectionStatus,
    chatSession,
    chatConfig,
    isCustomChatOpen,
    unreadCount,

    // 聊天历史管理状态
    isLoadingHistory,
    hasMoreHistory,
    historyPageSize,
    currentHistoryPage,
    allSessions,
    isLoadingSessions,

    // 原有计算属性
    isAvailable,
    isReady,
    canInitialize,

    // 自定义聊天界面计算属性
    isCustomChatConnected,
    hasUnreadMessages,
    canSendMessage,

    // 聊天历史管理计算属性
    totalMessages,
    userMessages,
    agentMessages,
    systemMessages,
    chatStats,

    // 原有方法
    loadConfig,
    loadLiveChatSDK,
    initializeChat,
    initializeChatWithCurrentUser,
    setUserInfo,
    setGuestInfo,
    transmitUserIdentity,
    validateUserIdentity,
    openChatWindow,
    closeChatWindow,
    minimizeChatWindow,
    checkAvailability,
    getCurrentUserForLiveChat,
    reset,
    clearError,

    // 自定义聊天界面方法
    initializeCustomChat,
    createChatSession,
    sendTextMessage,
    sendImageMessage,
    receiveMessage,
    validateImageFile,
    connectWebSocket,
    disconnectWebSocket,
    saveChatHistory,
    loadChatHistory,
    loadLocalChatHistory,
    cleanupExpiredHistory,
    updateUnreadCount,
    markAllMessagesAsRead,
    openCustomChat,
    closeCustomChat,

    // 聊天历史管理方法
    loadMoreHistory,
    getAllChatSessions,
    switchToSession,
    deleteSession,
    loadLocalChatSessions,
    saveLocalChatSessions
  }
})